import React, { FC, useState } from 'react';
import { makeStyles, Theme, Box, Grid, Button } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import { ExpandIcon } from 'svgs/subcontracting';
import { EngineeringIcon, ConstructionIcon } from 'svgs/subcontracting';
import U1CheckBox from 'components/atoms/U1CheckBox';
import clsx from 'clsx';
import { orderASC } from 'utils/sortHelper';

interface TestTypesOfWorkProps {
  onChangeTypesOfWork: (value: any) => void;
  typesOfWork: any;
  expandedConstruction: string | false;
  onChangeConstruction: any;
  expandedEngineering: string | false;
  onChangeEngineering: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },

  container: {
    justifyContent: 'space-between',
    width: '100%',
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },

  tab: {
    backgroundColor: theme.palette.background.default,
    color: 'transparent',
    border: `1px solid ${theme.palette.primary.contrastText}`,
    marginBottom: theme.spacing(3),
    width: '100%',
    minHeight: 72,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
  },
  span: {
    paddingLeft: theme.spacing(2),
    color: theme.palette.text.disabled,
  },
  tabActive: {
    backgroundColor: theme.palette.primary.main,
    color: 'transparent',
    border: `1px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(3),
    width: '100%',
    minHeight: 72,
    '& span': {
      color: theme.palette.common.white,
    },
    '& svg': {
      '& path': {
        fill: theme.palette.common.white,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  heading: {
    fontWeight: 500,
    marginLeft: theme.spacing(5),
  },
  tabPanel: {
    padding: 0,
    '& .MuiPaper-root': {
      borderRadius: 0,
      boxShadow: 'none',
      '& .MuiAccordionSummary-root': {
        minHeight: 50,
        '& .Mui-expanded': {
          minHeight: 'auto',
          margin: 0,
        },
      },
      '&:before': {
        display: 'none',
      },
    },
  },
  tabPanel1: {
    display: 'none',
  },
  accordion: {
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.primary.contrastText}`,
    background: theme.palette.background.default,
  },
  subAccordion: {
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
  },
  accordionDetails: {
    display: 'block',
    padding: theme.spacing(0, 2),
    background: theme.palette.common.white,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 6),
    },
  },
  accordionSummary: {
    position: 'relative',
    '& .MuiButtonBase-root': {
      position: 'absolute',
      left: 0,
      top: 8,
    },
  },
}));

const construction: Array<{ title: string; textItems: Array<{ subtitle: string; list?: Array<{ text: string }> }> }> = [
  {
    title: 'Communications',
    textItems: [
      {
        subtitle: 'A&E Services',
      },
      {
        subtitle: 'Aerial Fiber/Coaxial Construction',
      },
      {
        subtitle: 'Electric Transmission and Distribution Services',
      },
      {
        subtitle: 'Energy Storage and Battery System Design',
      },
      {
        subtitle: 'Engineering',
      },
      {
        subtitle: 'Fiber/Coax Splicing',
      },
      {
        subtitle: 'Grid Modernization',
      },
      {
        subtitle: 'LED Retrofits',
      },
      {
        subtitle: 'MDU',
      },
      {
        subtitle: 'Make Ready',
      },
      {
        subtitle: 'OSP/ISP fiber',
      },
      {
        subtitle: 'Site Acquisition and Permitting',
      },
      {
        subtitle: 'Solar Panel Installation',
      },
      {
        subtitle: 'Solar Site Acquisition',
      },
      {
        subtitle: 'Storm Remediation',
      },
      {
        subtitle: 'Tower Construction',
      },
      {
        subtitle: 'Underground Construction (Boring/Directional Drill; Plow/Trench)',
      },
      {
        subtitle: 'Underground Construction (Plow / Trench)',
      },
      {
        subtitle: 'Water and Sewer Pipeline Treatment Facilities',
      },
      {
        subtitle: 'Wind Farm Construction',
      },
    ],
  },
  {
    title: 'Electric Power Services',
    textItems: [
      {
        subtitle: 'Distribution Services',
        list: [
          { text: 'Distribution Construction and Maintenance' },
          { text: 'Distribution Design and Engineering' },
          { text: 'Installation of Both Overhead Pole Lines and Underground Buried Cable' },
          { text: 'Overhead Construction and Maintenance' },
          { text: 'Underground Construction and Maintenance' },
          { text: 'Underground Construction and Maintenance' },
          { text: 'Underground Cable Replacement' },
          { text: 'Pole Replacements' },
          { text: 'Line Relocations in Preparation for Road Widening Projects' },
          { text: 'Make-ready Work for Communication Infrastructure' },
          { text: 'Fault Locate and Repair (FLR)' },
        ],
      },
      {
        subtitle: 'Storm Response',
        list: [{ text: 'Transmission' }, { text: 'Substation' }, { text: 'Distribution' }, { text: 'EPC & Design/Build' }],
      },
      {
        subtitle: 'Substation Services',
        list: [
          { text: 'Substation Design' },
          { text: 'Substation Automation Including Technology Assessment & Network Planning' },
          { text: 'Design' },
          { text: 'Substation Testing & Commissioning' },
          { text: 'AIS & GIS Substations' },
          { text: 'Foundation Construction' },
          { text: 'Structural Steel Erection' },
          { text: 'Cable Trenching' },
          { text: 'Buss Work' },
        ],
      },
      {
        subtitle: 'Transmission Services',
        list: [
          { text: 'Transmission Construction and Maintenance' },
          { text: 'Energized Services' },
          { text: 'Design' },
          { text: 'Tower Erection' },
          { text: 'Exchange and Replacement of Towers' },
          { text: 'Power Duct Bank System Construction' },
          { text: 'Pre-construction Design and Engineering Consulting' },
          { text: 'Live Line Maintenance to Include Hot Sticking & Live Line Barehand' },
          { text: 'Right-of-way (ROW) Preparation and Clearing' },
          { text: 'Environmental Mitigation and Compliance' },
          { text: 'Turnkey Foundation Installation' },
          { text: 'Wood' },
          { text: 'Lattice Tower Assembly and Erection' },
          { text: 'Wire Stringing' },
          { text: 'Reconductoring of Existing Transmission Lines' },
          { text: 'Energization and Testing' },
          { text: '24/7 Emergency and Storm Response' },
        ],
      },
      {
        subtitle: 'Underground Electric Services',
        list: [
          { text: 'Vault Construction for Cable Splicing and Utility Hole Repair' },
          { text: 'Rebuild and Modifications' },
          { text: 'Fault Locate and Repair (Primary/Secondary/Street Light)' },
          { text: 'Cable Installation/Replacement (URD-DB/Network)' },
          { text: 'Cable Splicing 4kV 69kV Transformer and Switch Installation' },
          { text: 'Termination and Cutover' },
          { text: 'Conduit/Duct Bank Installation and Repair' },
          { text: 'Precast and Field Cast Utility Hole' },
          { text: 'Network Installation and Maintenance' },
          { text: 'Depth of Cover and Inclination Profile Surveys' },
          { text: 'Cathodic Profile Troubleshooting' },
          { text: 'AC Mitigation Testing' },
          { text: 'Facility Inspections' },
          { text: 'Data Analysis and Reporting' },
          { text: 'DC Interference Testing and Assessment' },
          { text: 'Utility Hole Repair' },
          { text: 'Distribution and Transmission Design and Construction' },
        ],
      },
    ],
  },
  {
    title: 'Wireless',
    textItems: [
      {
        subtitle: 'Structural Modification',
      },
      {
        subtitle: 'Mount Upgrades and Reinforcements',
      },
      {
        subtitle: 'DAS & Small Cell Installation',
      },
      {
        subtitle: 'Battery',
      },
      {
        subtitle: 'Tower Inspections',
      },
      {
        subtitle: 'LTE Antenna Installation and Upgrade',
      },
      {
        subtitle: 'Site Acquisition and Permitting',
      },
      {
        subtitle: 'Turn-Key Civil Site Development',
      },
      {
        subtitle: 'Radio Installation and Testing',
      },
      {
        subtitle: 'Microwave Installation and Pathing 1',
      },
      {
        subtitle: 'Emergency Repairs and Troubleshooting',
      },
      {
        subtitle: 'Testing and Integration',
      },
    ],
  },

  {
    title: 'Oil and Gas Services',
    textItems: [
      {
        subtitle: 'Pipeline Construction',
        list: [
          { text: 'New Pipeline Construction Integrity & Maintenance' },
          { text: 'Steel' },
          { text: 'HDPE' },
          { text: 'Composite (Fiberspar)' },
          { text: 'Zap-lock' },
          { text: 'PVC/Certalock' },
          { text: 'Fiberglass' },
          { text: 'Flowline/Well Connections' },
          { text: 'Hydrostatic Testing' },
          { text: 'Line Pigging/Commissioning Services' },
          { text: 'Fabrication Services' },
          { text: 'HDD Boring Services' },
          { text: 'Sandblasting/ Painting' },
          { text: 'Roustabout Services' },
          { text: 'Fast Fusio' },
          { text: 'Pipeline Integrity Management and Corrosion' },
          { text: 'Mains and Service Installation' },
          { text: 'New Business and Joint Trench' },
          { text: 'Maintenance' },
          { text: 'Horizontal Directional Drilling' },
          { text: 'Service Renewal' },
        ],
      },
    ],
  },
  {
    title: 'Renewable Energy',
    textItems: [
      {
        subtitle: 'Solar Energy Construction -EPC',
        list: [
          { text: 'Civil/Road/Earthwork Construction' },
          { text: 'Substation and Switchyard Construction' },
          { text: 'Transmission Line Construction' },
          { text: 'Piling and Racking Installation' },
          { text: 'Panel/Module Installation' },
          { text: 'Electrical Trenching and Cabling Installation (AC & DC)' },
          { text: 'Electrical Terminations (AC & DC)' },
          { text: 'Equipment Placement' },
          { text: 'Commissioning and Turnover' },
          { text: 'Operations and Maintenance Service' },
        ],
      },
      {
        subtitle: 'Wind Energy Construction -EPC and BOP',
        list: [
          { text: 'Civil/Road/Earthwork Construction' },
          { text: 'Foundation Design and Construction' },
          { text: 'Tower and Turbine Erection and Installation' },
          { text: 'Turbine and Rotor Assembly' },
          { text: 'Tower Wiring/FAA Light Install' },
          { text: 'Electrical Design and Installation of the Substation' },
          { text: 'Electrical Terminations' },
          { text: 'Commissioning and Turnover' },
          { text: 'NCCCO Certified Crane Services' },
          { text: 'Design Services (Foundation, Civil, Electrical)' },
        ],
      },
    ],
  },
  {
    title: 'Water and Sewer',
    textItems: [
      {
        subtitle: 'Treatment Plants',
      },
      {
        subtitle: 'Pump & Lift Stations',
      },
      {
        subtitle: 'Whater Transmission Mains',
      },
      {
        subtitle: 'Wastewater Force Mains',
      },
      {
        subtitle: 'Storm Sewer Trunk Lines',
      },
      {
        subtitle: 'Utility Rehabilitation Lines',
      },
      {
        subtitle: 'Maintenance/Emergency Restoration',
      },
      {
        subtitle: 'Pipeline and Facility Construction',
      },
    ],
  },
];

const engineering: Array<{ title: string; textItems: Array<{ subtitle: string; list?: Array<{ text: string }> }> }> = [
  {
    title: 'Solar Engineering',
    textItems: [
      {
        subtitle: 'Conceptual Design and Feasibility',
      },
      {
        subtitle: 'Energy Usage Analyses',
      },
      {
        subtitle: 'Energy Production Estimates',
      },
      {
        subtitle: 'PV Design & Layout',
      },
      {
        subtitle: 'RFP Preparation',
      },
      {
        subtitle: 'Electrical Engineering',
      },
      {
        subtitle: 'Solar Engineering - Investigation',
      },
      {
        subtitle: 'CAD Drafting',
      },
      {
        subtitle: 'Construction Administration',
      },
      {
        subtitle: 'Project Management',
      },
      {
        subtitle: 'Civil & Environmental Engineering',
      },
      {
        subtitle: 'Commissioning Support',
      },
      {
        subtitle: 'Independent Engineer Review',
      },
      {
        subtitle: 'Permitting & Applications',
      },
      {
        subtitle: 'Project Evaluation and Cost Estimation for Solar Services',
      },
      {
        subtitle: 'Site Surveys',
      },
      {
        subtitle: 'Electrical Planning and PV Layout',
      },
      {
        subtitle: 'System Construction Installations',
      },
      {
        subtitle: 'System Upgrades and Expansion',
      },
      {
        subtitle: 'Equipment Specifications Consultation',
      },
      {
        subtitle: 'System Analysis and Feasibility Studies',
      },
      {
        subtitle: 'Compliance Requirements',
      },
    ],
  },
  {
    title: 'Wind Turbine Engineering Services',
    textItems: [
      {
        subtitle: 'Advancement and Authorization',
      },
      {
        subtitle: 'Wind Farm Design',
      },
      {
        subtitle: 'Grid Connection Design and Transmission',
      },
      {
        subtitle: 'SWPPP',
      },
    ],
  },
  {
    title: 'Electrical Engineering',
    textItems: [
      {
        subtitle: 'Feasibility Studies',
      },
      {
        subtitle: 'Master Planning',
      },
      {
        subtitle: 'Asset Management',
      },
      {
        subtitle: 'NERC Resolutions',
      },
      {
        subtitle: 'Reliability Projects',
      },
      {
        subtitle: 'Ampacity Calculation',
      },
      { subtitle: 'System Load Studies' },
      { subtitle: 'Voltage Drop Calculations' },
      { subtitle: 'Field Walk Downs' },
      { subtitle: 'Electric Equipment Sizing' },
      { subtitle: 'Distribution Automation (DA) 12kV and 34kV Reclosers' },
      { subtitle: 'Network Equipment Vaults Physical Layout' },
      { subtitle: 'Storm Hardening and Grid Resiliency' },
      { subtitle: 'Summer/Winter Critical' },
      { subtitle: 'Solar/Photovoltaic Designs' },
      { subtitle: 'Back-up Generator Designs' },
      { subtitle: 'Plan and Profile Conduit Ductbank Designs' },
      { subtitle: 'Manhole and Vault Designs' },
      { subtitle: 'Construction Support' },
      { subtitle: 'Cable Sizing' },
    ],
  },
  {
    title: 'Telecommunications Engineering',
    textItems: [
      { subtitle: 'Feasibility Studies' },
      { subtitle: 'Wiring Diagrams' },
      { subtitle: 'Termination Panel Diagrams' },
      { subtitle: 'Rack Layout and Raceway Design' },
      { subtitle: 'MDF/IDF Layout Design' },
      { subtitle: 'Plan & Profile Design Drawings' },
      { subtitle: 'Pole Inventory' },
      { subtitle: 'Foundation Penetration Design' },
      { subtitle: 'Renderings' },
      { subtitle: 'Interior Conduit Routing Design' },
      { subtitle: 'Riser Diagrams and Design' },
      { subtitle: 'Fiber Splicing Trees and Diagrams' },
      { subtitle: 'Over-lash and Make Ready Designs' },
      { subtitle: 'OSP Engineering' },
      { subtitle: 'Fiber Optic Cable Systems (FTTX)' },
      { subtitle: 'Emergency Maintenance' },
    ],
  },
  {
    title: 'Wireless Engineering',
    textItems: [
      { subtitle: 'Complete Network and RF System Architecture' },
      { subtitle: 'Capacity Planning and Forecasting' },
      { subtitle: 'Network Design and System Objectives' },
      { subtitle: 'RF Coverage and Performance Models' },
      { subtitle: 'LTE Fixed and Mobile Network Build-outs' },
      { subtitle: 'Licensing and Frequency Coordination Support' },
      { subtitle: 'Base Stations' },
      { subtitle: 'Field Support Engineering Services' },
      { subtitle: 'Microwave Radio Design (Licensed or Unlicensed)' },
      { subtitle: 'CBRS Feasibility Testing and Recommendation' },
      { subtitle: 'Drive Test Management and Annual System Verification' },
      { subtitle: 'Mobile Switching Office' },
      { subtitle: 'Data and Internet Access Systems' },
      { subtitle: 'Long Range Planning Support' },
      { subtitle: 'Spectrum Evaluation and Interference Management' },
      { subtitle: 'Manage and Negotiate Contour Extensions' },
      { subtitle: 'Construction Management Services' },
      { subtitle: 'Transport/Backhaul and IP Design' },
      { subtitle: 'Problem Isolation and Repair' },
      { subtitle: 'Site Acquisition' },
      { subtitle: 'A&E Services' },
      { subtitle: 'Small Cell & 5G' },
      { subtitle: 'Public Safety' },
      { subtitle: 'DAS & In-Building Systems' },
      { subtitle: 'Structural Analysis' },
      { subtitle: 'Tower/Foundation Analysis & Reinforcement' },
      { subtitle: 'CAD Drafting' },
      { subtitle: 'Utility Coordination' },
      { subtitle: 'Certified Special Inspections' },
      { subtitle: 'Project & Construction Management' },
    ],
  },
  {
    title: 'Oil and Gas Engineering',
    textItems: [
      { subtitle: 'Nodal Analysis' },
      { subtitle: 'Tubing Design & APB Analysis' },
      { subtitle: 'Wellbore Schematics' },
      { subtitle: 'Detailed Engineering' },
      { subtitle: 'Rig Site Support' },
      { subtitle: 'Post-Job Analysis' },
      { subtitle: 'Initial Site Walk-down' },
      { subtitle: 'Underground Structure Assessments' },
      { subtitle: 'Site Topographic Survey' },
      { subtitle: 'Existing Easement Research and New Easement Acquisition' },
      { subtitle: 'Mechanical System Design' },
      { subtitle: 'System Modeling Utilizing Software Platforms' },
      { subtitle: 'Gas Design in Client GIS System Software Platforms and CAD Drawings' },
      { subtitle: 'Utilization of Client Software Platforms for Ordering of Materials and Construction Cost Estimating' },
      { subtitle: 'AutoCAD Drawings for New or Revised Infrastructure' },
      { subtitle: 'Civil Pipeline Plan & Profile Drawings' },
      { subtitle: 'Permit Acquisition â€“ State' },
      { subtitle: 'Municipal / Customer Outreach and Coordination' },
      { subtitle: 'Post-Design Walk-downs' },
      { subtitle: 'Pipeline Installation Inspection Services (Pipeline Pressure Tests, Fusion Welds, Steel Welds etc.)' },
      { subtitle: 'Record Survey Performance and Data Incorporation in Client Systems and CAD Drawings' },
    ],
  },
  {
    title: 'Water & Wastewater Engineering',
    textItems: [
      { subtitle: 'Master Planning' },
      { subtitle: 'Feasibility Studies' },
      { subtitle: 'Regulatory Permitting' },
      { subtitle: 'Water/Wastewater System Modeling' },
      { subtitle: 'Water Treatment' },
      { subtitle: 'Wastewater Treatment' },
      { subtitle: 'Groundwater Withdrawal Permitting and Design' },
      { subtitle: 'Feasibility Studies' },
      { subtitle: 'Master Planning' },
      { subtitle: 'Asset Management' },
      { subtitle: 'Hydraulic Analysis' },
      { subtitle: 'Metering' },
      { subtitle: 'Process Monitoring' },
      { subtitle: 'Pump/Lift Stations' },
      { subtitle: 'Slip lining' },
      { subtitle: 'Treatment Systems' },
      { subtitle: 'Telemetry' },
      { subtitle: 'Line Stops' },
      { subtitle: 'Valve Replacement' },
      { subtitle: 'Utility Coordination' },
      { subtitle: 'Treatment/Filtration' },
      { subtitle: 'Cleaning and Lining' },
      { subtitle: 'Odor Control Design' },
      { subtitle: 'Transmission and Distribution Mains' },
      { subtitle: 'Geotechnical Exploration Plans' },
      { subtitle: 'Service Mains and Connections' },
      { subtitle: 'System and Water Quality Modeling' },
      { subtitle: 'Chemical Feed Systems' },
      { subtitle: 'Chlorination/De-chlorination' },
      { subtitle: 'GIS Mapping (Locations of Pipe)' },
      { subtitle: 'Subsurface Utility Engineering / Exploration' },
      { subtitle: 'Construction Administration' },
      { subtitle: 'Heavy Lifts & Below the Hook Lifting Devices' },
      { subtitle: 'Value Engineering' },
      { subtitle: 'Elevated Storage Tanks' },
      { subtitle: 'CSO Storage and Outfalls' },
      { subtitle: 'NASSCO Certifications PACP' },
      { subtitle: 'Construction Specifications and Estimates' },
    ],
  },
  {
    title: 'Structural Engineering',
    textItems: [
      { subtitle: 'New Building Design' },
      { subtitle: 'Building Evaluations' },
      { subtitle: 'Seismic Retrofit' },
      { subtitle: 'Structural Renovations' },
      { subtitle: 'Building Additions' },
      { subtitle: 'Equipment Supports' },
      { subtitle: 'Crane Rigging Designs' },
      { subtitle: 'Shoring Design' },
      { subtitle: 'Building Risk Assessment' },
      { subtitle: 'Forensic Engineering' },
      { subtitle: 'Failure Analysis' },
      { subtitle: 'Construction Litigation and Claims Evaluation' },
      { subtitle: 'Due Diligence' },
      { subtitle: 'Structural Anchorage' },
      { subtitle: 'Bridge Erection Designs' },
    ],
  },
  {
    title: 'Geotechnical Engineering',
    textItems: [
      { subtitle: 'Geotechnical Baseline Reports' },
      { subtitle: 'Asset Management' },
      { subtitle: 'Formwork & Falsework' },
      { subtitle: 'Cofferdam Design' },
      { subtitle: 'Blasting Design' },
      { subtitle: 'Value Engineering' },
      { subtitle: 'Slip Lining' },
      { subtitle: 'Ground Improvement Designs' },
      { subtitle: 'Construction Administration' },
      { subtitle: 'Reservoirs and Dams' },
      { subtitle: 'Landslide Evaluation and Stabilization Design' },
      { subtitle: 'Tunnel Risk Management' },
      { subtitle: 'Seismic Hazards Evaluation and Mitigation' },
      { subtitle: 'Geotechnical Exploration Plans' },
      { subtitle: 'Heavy Lifts & Below the Hook Lifting Devices' },
      { subtitle: 'Earth Excavation Support System Designs' },
      { subtitle: 'Existing Infrastructure Assessment and Inspection' },
      { subtitle: 'Subsurface Geological Assessment' },
      { subtitle: '3D Finite Element Modeling' },
      { subtitle: 'Construction Specifications and Estimates' },
      { subtitle: 'Data Management and Controls' },
    ],
  },
];

const TypesOfWork: FC<TestTypesOfWorkProps> = (props) => {
  const {
    onChangeTypesOfWork,
    typesOfWork,
    expandedConstruction,
    onChangeConstruction,
    expandedEngineering,
    onChangeEngineering,
  } = props;
  const classes = useStyles();
  const [tabValue, setTabValue] = useState<number>(1);

  const handleChange = (newTabValue) => () => setTabValue(newTabValue);

  const onToggle = (id: { root: string; title: string; subtitle: string; text?: string }) => (e, checked: boolean) => {
    const { root, title, subtitle, text } = id;
    const checkBoxes = { ...typesOfWork };
    if (checkBoxes[root] === undefined) {
      checkBoxes[root] = {};
    }
    const tempRoot = checkBoxes[root];
    if (text) {
      if (tempRoot[title] === undefined) {
        tempRoot[title] = {};
      }
      if (tempRoot[title][subtitle] === undefined) {
        tempRoot[title][subtitle] = [];
      }
      const temp = tempRoot[title][subtitle];
      const value = text;
      if (checked) {
        temp.push(value);
      } else {
        const index = temp.indexOf(value);
        if (index >= 0) {
          temp.splice(index, 1);
        }
        if (!temp.length) {
          delete tempRoot[title][subtitle];
        }
        if (!Object.keys(tempRoot[title]).length) {
          delete tempRoot[title];
        }
      }
    } else {
      if (tempRoot[title] === undefined) {
        tempRoot[title] = [];
      }
      const temp = tempRoot[title];
      const value = subtitle;
      if (checked) {
        temp.push(value);
      } else {
        const index = temp.indexOf(value);
        if (index >= 0) {
          temp.splice(index, 1);
        }
        if (!temp.length) {
          delete tempRoot[title];
        }
      }
    }

    if (!Object.keys(checkBoxes[root]).length) {
      delete checkBoxes[root];
    }

    onChangeTypesOfWork(checkBoxes);
  };

  return (
    <Box className={classes.root}>
      <Grid container direction="row" justify="space-between" spacing={2}>
        <Grid xs={6} item>
          <Button className={clsx(classes.tab, tabValue === 1 && classes.tabActive)} onClick={handleChange(1)}>
            <Box display="flex" flexDirection="column">
              <ConstructionIcon style={{ margin: '0 auto 6px' }} />
              <span className={classes.span}>CONSTRUCTION</span>
            </Box>
          </Button>
        </Grid>
        <Grid xs={6} item>
          <Button className={clsx(classes.tab, tabValue === 2 && classes.tabActive)} onClick={handleChange(2)}>
            <Box display="flex" flexDirection="column">
              <EngineeringIcon style={{ margin: '0 auto 6px' }} />
              <span className={classes.span}>ENGINEERING</span>
            </Box>
          </Button>
        </Grid>
      </Grid>
      <Box className={classes.tabPanel}>
        <div style={{ display: tabValue === 1 ? 'block' : 'none' }}>
          {orderASC(construction, 'title').map(({ title, textItems }, index) => (
            <Accordion
              expanded={expandedConstruction === `panel_${index}`}
              onChange={onChangeConstruction(`panel_${index}`)}
              className={classes.accordion}
              key={index}
            >
              <AccordionSummary
                expandIcon={<ExpandIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                className={classes.accordionSummary}
              >
                <Typography className={classes.heading} id={'titleConstruction'}>
                  {title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                {orderASC(textItems, 'subtitle').map(({ subtitle, list }, subIndex) =>
                  !list?.length ? (
                    <Box key={subIndex} mt={1}>
                      <U1CheckBox
                        text={subtitle}
                        onChange={onToggle({ root: 'CONSTRUCTION', title, subtitle })}
                        checked={
                          !!typesOfWork.CONSTRUCTION &&
                          typesOfWork.CONSTRUCTION[title] &&
                          typesOfWork.CONSTRUCTION[title].includes(subtitle)
                        }
                      />
                    </Box>
                  ) : (
                    <Accordion className={classes.subAccordion} key={subIndex}>
                      <AccordionSummary
                        expandIcon={<ExpandIcon />}
                        aria-controls="panel1a-content"
                        id="sub-panel1a-header"
                        className={classes.accordionSummary}
                      >
                        <Typography className={classes.heading}>{subtitle}</Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        {orderASC(list, 'text').map(({ text }, subIndex2) => (
                          <Box key={subIndex2} mt={1}>
                            <U1CheckBox
                              text={text}
                              onChange={onToggle({ root: 'CONSTRUCTION', title, subtitle, text })}
                              checked={
                                !!typesOfWork.CONSTRUCTION &&
                                typesOfWork.CONSTRUCTION[title] &&
                                typesOfWork.CONSTRUCTION[title][subtitle] &&
                                typesOfWork.CONSTRUCTION[title][subtitle].includes(text)
                              }
                            />
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ),
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
        <div style={{ display: tabValue === 2 ? 'block' : 'none' }}>
          {orderASC(engineering, 'title').map(({ title, textItems }, index) => (
            <Accordion
              className={classes.accordion}
              key={index}
              expanded={expandedEngineering === `panel_${index}`}
              onChange={onChangeEngineering(`panel_${index}`)}
            >
              <AccordionSummary
                expandIcon={<ExpandIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                className={classes.accordionSummary}
              >
                <Typography className={classes.heading} id={'titleEngineering'}>
                  {title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                {orderASC(textItems, 'subtitle').map(({ subtitle, list }, subIndex) =>
                  !list?.length ? (
                    <Box key={subIndex}>
                      <U1CheckBox
                        text={subtitle}
                        onChange={onToggle({ root: 'ENGINEERING', title, subtitle })}
                        checked={
                          !!typesOfWork.ENGINEERING &&
                          typesOfWork.ENGINEERING[title] &&
                          typesOfWork.ENGINEERING[title].includes(subtitle)
                        }
                      />
                    </Box>
                  ) : (
                    <Accordion className={classes.subAccordion} key={subIndex}>
                      <AccordionSummary
                        expandIcon={<ExpandIcon />}
                        aria-controls="panel1a-content"
                        id="sub-panel1a-header"
                        className={classes.accordionSummary}
                      >
                        <Typography className={classes.heading}>{subtitle}</Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                        {orderASC(list, 'text').map(({ text }, subIndex2) => (
                          <Box key={subIndex2}>
                            <U1CheckBox
                              text={text}
                              onChange={onToggle({ root: 'ENGINEERING', title, subtitle, text })}
                              checked={
                                !!typesOfWork.ENGINEERING &&
                                typesOfWork.ENGINEERING[title] &&
                                typesOfWork.ENGINEERING[title][subtitle] &&
                                typesOfWork.ENGINEERING[title][subtitle].includes(text)
                              }
                            />
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ),
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </Box>
    </Box>
  );
};

export default TypesOfWork;
